/**
 * The Wild Turtle — predictive-search.js
 * Shopify predictive search API integration
 */

(() => {
  'use strict';

  const searchEl     = document.getElementById('predictive-search');
  const inputEl      = document.getElementById('predictive-search-input');
  const clearBtn     = document.getElementById('predictive-search-clear');
  const itemsEl      = document.getElementById('predictive-search-items');
  const loadingEl    = document.querySelector('.predictive-search__loading');
  const searchToggle = document.getElementById('search-toggle');

  if (!searchEl || !inputEl) return;

  const predictiveURL = window.themeData?.routes?.predictive || '/search/suggest.json';
  let debounceTimer;

  /* =========================================================
     Format money
  ========================================================= */
  function formatMoney(cents) {
    const fmt = window.themeData?.money_format || '${{amount}}';
    return fmt.replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2));
  }

  /* =========================================================
     Fetch suggestions
  ========================================================= */
  async function fetchSuggestions(query) {
    const url = new URL(predictiveURL, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,collection,article,page');
    url.searchParams.set('resources[limit]', '5');
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor');

    const res = await fetch(url.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!res.ok) throw new Error('Search failed');
    return res.json();
  }

  /* =========================================================
     Render results
  ========================================================= */
  function renderResults(data, query) {
    if (!itemsEl) return;

    const results = data.resources?.results || {};
    const products    = results.products    || [];
    const collections = results.collections || [];
    const articles    = results.articles    || [];
    const pages       = results.pages       || [];

    if (!products.length && !collections.length && !articles.length) {
      itemsEl.innerHTML = `
        <div class="predictive-search__no-results">
          <p>No results for "<strong>${escapeHtml(query)}</strong>"</p>
          <a href="/search?q=${encodeURIComponent(query)}" class="btn btn--secondary btn--sm">
            Search all products
          </a>
        </div>`;
      return;
    }

    let html = '';

    if (products.length) {
      html += `<div class="predictive-search__section"><h3 class="predictive-search__section-title">Products</h3>`;
      products.forEach(p => {
        const img = p.featured_image
          ? `<img src="${p.featured_image}" alt="${escapeHtml(p.title)}" loading="lazy">`
          : `<div class="predictive-search__result-placeholder"></div>`;
        const price = p.price ? formatMoney(p.price) : '';
        html += `
          <a href="${p.url}" class="predictive-search__result">
            <div class="predictive-search__result-image">${img}</div>
            <div class="predictive-search__result-info">
              <span class="predictive-search__result-title">${highlightMatch(escapeHtml(p.title), query)}</span>
              ${p.vendor ? `<span class="predictive-search__result-vendor">${escapeHtml(p.vendor)}</span>` : ''}
              ${price ? `<span class="predictive-search__result-price">${price}</span>` : ''}
            </div>
          </a>`;
      });
      html += '</div>';
    }

    if (collections.length) {
      html += `<div class="predictive-search__section"><h3 class="predictive-search__section-title">Collections</h3>`;
      collections.forEach(c => {
        html += `
          <a href="${c.url}" class="predictive-search__result predictive-search__result--simple">
            <span class="predictive-search__result-title">${highlightMatch(escapeHtml(c.title), query)}</span>
          </a>`;
      });
      html += '</div>';
    }

    if (articles.length) {
      html += `<div class="predictive-search__section"><h3 class="predictive-search__section-title">Articles</h3>`;
      articles.slice(0, 2).forEach(a => {
        html += `
          <a href="${a.url}" class="predictive-search__result predictive-search__result--simple">
            <span class="predictive-search__result-title">${highlightMatch(escapeHtml(a.title), query)}</span>
          </a>`;
      });
      html += '</div>';
    }

    html += `
      <div class="predictive-search__footer">
        <a href="/search?q=${encodeURIComponent(query)}" class="predictive-search__view-all">
          View all results for "<strong>${escapeHtml(query)}</strong>"
        </a>
      </div>`;

    itemsEl.innerHTML = html;
  }

  function highlightMatch(text, query) {
    const re = new RegExp(`(${escapeRegex(query)})`, 'gi');
    return text.replace(re, '<mark>$1</mark>');
  }

  function escapeHtml(str) {
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
    return String(str).replace(/[&<>"']/g, m => map[m]);
  }

  function escapeRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /* =========================================================
     Input handling
  ========================================================= */
  inputEl.addEventListener('input', () => {
    const query = inputEl.value.trim();
    clearBtn?.toggleAttribute('hidden', !query);

    clearTimeout(debounceTimer);

    if (query.length < 2) {
      if (itemsEl) itemsEl.innerHTML = '';
      return;
    }

    debounceTimer = setTimeout(async () => {
      loadingEl?.removeAttribute('hidden');
      try {
        const data = await fetchSuggestions(query);
        renderResults(data, query);
      } catch (err) {
        console.error('Predictive search error:', err);
      } finally {
        loadingEl?.setAttribute('hidden', '');
      }
    }, 300);
  });

  clearBtn?.addEventListener('click', () => {
    inputEl.value = '';
    if (itemsEl) itemsEl.innerHTML = '';
    clearBtn.setAttribute('hidden', '');
    inputEl.focus();
  });

  // Handle form submit — redirect to search page
  searchEl.closest('form')?.addEventListener('submit', e => {
    if (!inputEl.value.trim()) e.preventDefault();
  });

  /* =========================================================
     CSS for predictive search
  ========================================================= */
  const style = document.createElement('style');
  style.textContent = `
    #predictive-search {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: var(--z-modal);
      background: rgba(255,255,255,.98);
      backdrop-filter: blur(12px);
      display: flex;
      flex-direction: column;
      padding: var(--sp-4) var(--sp-6);
      overflow-y: auto;
    }

    #predictive-search[hidden] { display: none; }

    .predictive-search__form {
      display: flex;
      align-items: center;
      gap: var(--sp-4);
      padding-bottom: var(--sp-4);
      border-bottom: 1px solid var(--color-border);
      margin-bottom: var(--sp-4);
      max-width: 700px;
      margin-inline: auto;
      width: 100%;
    }

    .predictive-search__input-wrap {
      flex: 1;
      position: relative;
      display: flex;
      align-items: center;
    }

    .predictive-search__icon {
      position: absolute;
      left: var(--sp-3);
      color: var(--color-text-muted);
      width: 20px;
      height: 20px;
    }

    .predictive-search__input {
      width: 100%;
      padding: var(--sp-3) var(--sp-4) var(--sp-3) var(--sp-10);
      border: 2px solid var(--color-dark-teal);
      border-radius: var(--radius-full);
      font-size: var(--fs-lg);
      font-family: var(--font-body);
      background: var(--color-white);
      outline: none;
    }

    .predictive-search__clear {
      position: absolute;
      right: var(--sp-3);
      color: var(--color-text-muted);
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .predictive-search__close {
      font-family: var(--font-heading);
      font-size: var(--fs-sm);
      font-weight: var(--fw-semi);
      color: var(--color-dark-teal);
      white-space: nowrap;
    }

    .predictive-search__results {
      max-width: 700px;
      margin-inline: auto;
      width: 100%;
    }

    .predictive-search__section { margin-bottom: var(--sp-5); }

    .predictive-search__section-title {
      font-family: var(--font-heading);
      font-size: var(--fs-xs);
      font-weight: var(--fw-bold);
      text-transform: uppercase;
      letter-spacing: 0.1em;
      color: var(--color-text-muted);
      margin-bottom: var(--sp-2);
      padding: var(--sp-2) 0;
      border-bottom: 1px solid var(--color-border);
    }

    .predictive-search__result {
      display: flex;
      align-items: center;
      gap: var(--sp-3);
      padding: var(--sp-3) var(--sp-2);
      border-radius: var(--radius-md);
      transition: background var(--transition-fast);
    }

    .predictive-search__result:hover {
      background: var(--color-sage-mist);
    }

    .predictive-search__result-image {
      width: 52px;
      height: 52px;
      flex-shrink: 0;
      border-radius: var(--radius-sm);
      overflow: hidden;
      background: var(--color-sage-mist);
    }

    .predictive-search__result-image img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .predictive-search__result-info {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .predictive-search__result-title {
      font-family: var(--font-heading);
      font-size: var(--fs-sm);
      font-weight: var(--fw-semi);
      color: var(--color-text);
    }

    .predictive-search__result-title mark {
      background: none;
      color: var(--color-dark-teal);
      font-weight: var(--fw-bold);
    }

    .predictive-search__result-vendor {
      font-size: var(--fs-xs);
      color: var(--color-text-muted);
    }

    .predictive-search__result-price {
      font-family: var(--font-heading);
      font-size: var(--fs-sm);
      font-weight: var(--fw-bold);
      color: var(--color-dark-teal);
    }

    .predictive-search__result--simple { padding: var(--sp-2) var(--sp-2); }

    .predictive-search__no-results {
      text-align: center;
      padding: var(--sp-12) var(--sp-4);
      color: var(--color-text-muted);
    }

    .predictive-search__no-results p { margin-bottom: var(--sp-4); }

    .predictive-search__footer {
      padding: var(--sp-4) var(--sp-2);
      border-top: 1px solid var(--color-border);
      margin-top: var(--sp-4);
    }

    .predictive-search__view-all {
      font-family: var(--font-heading);
      font-size: var(--fs-sm);
      font-weight: var(--fw-semi);
      color: var(--color-dark-teal);
    }

    .predictive-search__view-all:hover { text-decoration: underline; }

    .predictive-search__loading {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--sp-8);
    }

    .predictive-search__spinner {
      width: 28px;
      height: 28px;
      border: 3px solid var(--color-border);
      border-top-color: var(--color-dark-teal);
      border-radius: 50%;
      animation: spin 0.7s linear infinite;
    }
  `;
  document.head.appendChild(style);

})();
